import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appelApi } from '../lib/api';
import { ContexteMarqueReact, type ApercuMarque } from './contexte';
import {
  CLE_MARQUE,
  NOM_AVANT_CHARGEMENT,
  marqueMemorisee,
  memoriserMarque,
  poserNuances,
  poserTitreDocument,
  type MarquePublique,
} from './marque';
import { NUANCES_AVANT_CHARGEMENT } from './nuances-avant-chargement';

/**
 * Habille toute l'interface — tous les espaces et les pages publiques — de la
 * marque en vigueur (TF-436), ou de l'aperçu que la direction regarde. Monté à
 * la racine, au-dessus de la session : la page de connexion en a besoin.
 */
export function FournisseurMarque({ children }: { children: ReactNode }): JSX.Element {
  const { data } = useQuery({
    queryKey: CLE_MARQUE,
    queryFn: () => appelApi<MarquePublique>('/marque'),
    // Une marque appliquée se voit au retour sur l'onglet, sans recharger.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
  const [apercu, setApercu] = useState<ApercuMarque | null>(null);

  const enVigueur = useMemo<MarquePublique>(
    () => data ?? marqueMemorisee() ?? { nom: NOM_AVANT_CHARGEMENT, nuances: NUANCES_AVANT_CHARGEMENT, logo: null },
    [data],
  );
  const marque = apercu?.marque ?? enVigueur;

  useEffect(() => {
    if (data) memoriserMarque(data);
  }, [data]);

  useEffect(() => {
    poserNuances(document.documentElement, marque.nuances);
    poserTitreDocument({ nom: marque.nom });
  }, [marque]);

  const demarrerApercu = useCallback((nouveau: ApercuMarque) => setApercu(nouveau), []);
  const arreterApercu = useCallback(() => setApercu(null), []);

  const valeur = useMemo(
    () => ({ marque, apercu, demarrerApercu, arreterApercu }),
    [marque, apercu, demarrerApercu, arreterApercu],
  );
  return <ContexteMarqueReact.Provider value={valeur}>{children}</ContexteMarqueReact.Provider>;
}
